import {useAuth0} from '@auth0/auth0-react';
import Button from 'Components/Atoms/Button/Button';
import Card from 'Components/Atoms/Card/Card';
import CardFooter from 'Components/Atoms/Card/CardFooter/CardFooter';
import CardHeader from 'Components/Atoms/Card/CardHeader/CardHeader';
import Container from 'Components/Atoms/Container/Container';
import EntityIcon from 'Components/Atoms/EntityIcon/EntityIcon';
import Loader from 'Components/Atoms/Loader/Loader';
import Text from 'Components/Atoms/Text/Text';
import Title from 'Components/Atoms/Title/Title';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import DisplayName from 'Components/Molecules/DisplayName/DisplayName';
import FormGroup from 'Components/Molecules/Form/FormGroup/FormGroup';
import FormSection from 'Components/Molecules/Form/FormSection/FormSection';
import DeletionModal from 'Components/Molecules/Modals/Actions/DeletionModal/DeletionModal';
import TitleGroup from 'Components/Molecules/TitleGroup/TitleGroup';
import Contact from 'Components/Organisms/Contact/Contact';
import {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import {STATUS} from 'Services/Status/Status.types';
import {SUBSTATUS} from 'Services/Status/Substatus.types';
import {
    createBusinessUnit,
    deleteBusinessUnit,
    fetchBusinessUnitConcept,
    fetchBusinessUnitV2,
    fetchBusinessUnitValues,
    updateBusinessUnit,
    upsertBusinessUnitValues
} from 'Store/BusinessUnit/Actions';
import {getBusinessUnitByIdAsObject} from 'Store/BusinessUnit/Selectors';
import {
    createCandidate,
    deleteCandidate,
    fetchCandidateConcept,
    fetchCandidateV2,
    fetchCandidateValues,
    updateCandidate,
    upsertCandidateValues
} from 'Store/Candidate/Actions';
import {getCandidateByIdAsObject} from 'Store/Candidate/Selectors';
import {
    createContact,
    deleteContact,
    fetchContactConcept,
    fetchContactV2,
    fetchContactValues,
    updateContact,
    upsertContactValues
} from 'Store/Contact/Actions';
import {getContactByIdAsObject} from 'Store/Contact/Selectors';
import {StoreState} from 'Store/Reducers';
import {getEntitiesById, getEntityById} from 'Store/Settings/Entities/Selectors';
import {getEntityTypesByEntityIdAsObject, getEntityTypesByIdAsObject} from 'Store/Settings/EntityTypes/Selector';
import {getFieldsById} from 'Store/Settings/Fields/Selectors';
import {getLayoutsByEntityTypeIdAsObject} from 'Store/Settings/Layout/Selectors';
import {
    isEntityFetchingByEntityName,
    selectConceptRecordIdByEntityName,
    selectRecordsByIdsByEntityName,
    selectValuesByIdsByEntityName
} from 'Store/Shared/Store/Values/Selector';
import {updateUser} from 'Store/User/Actions';
import {
    createVacancy,
    deleteVacancy,
    fetchVacancyConcept,
    fetchVacancyV2,
    fetchVacancyValues,
    updateVacancy,
    upsertVacancyValues
} from 'Store/Vacancy/Actions';
import {getVacancyByIdAsObject} from 'Store/Vacancy/Selectors';
import {v4 as uuidv4} from 'uuid';
import CategoryOverview from '../EntityCreateFlow/CategoryOverview/CategoryOverview';
import CategoryStep from '../EntityCreateFlow/CategoryStep/CategoryStep';
import EntityRelationOverview from '../EntityCreateFlow/EntityRelationOverview/EntityRelationOverview';
import EntityRelationStep from '../EntityCreateFlow/EntityRelationStep/EntityRelationStep';
import EntityTypeStep from '../EntityCreateFlow/EntityTypeStep/EntityTypeStep';
import styles from './EntityCreate.module.sass';
import EntityCreateContactModule from './EntityCreateContactModule/EntityCreateContactModule';
import EntityCreateStepsDot from './EntityCreateStepsDot/EntityCreateStepsDot';
import {useEntityRecord} from 'Services/Api/useEntityRecord/useEntityRecord';
import useFetch from 'Api/useFetch';
import {getOfferById, getOfferByIdAsObject} from 'Store/Offer/Selectors';
import {
    createOffer,
    deleteOffer,
    fetchOfferConcept,
    fetchOfferValues,
    upsertOffer,
    updateOffer,
    fetchOffer,
    upsertOfferValues
} from 'Store/Offer/Actions';
import Files from 'Components/Organisms/Files/Files';
import Mailer from 'Components/Organisms/Mailer/Mailer';

export const STEP = {
    ENTITY_TYPE: 'entity_type',
    DISPLAY_NAME: 'display_name',
    UPLOAD_FILES: 'upload_files',
    MAIL: 'mail',
    ENTITY_RELATION: 'entity_relation',
    MODULE_CONTACT: 'module_contact',
    PRIMARY_FIELDS: 'primary_fields'
}

const EntityCreate = ({
    children,
    entity_id,

    isEntityFetching,
    entity, layouts, conceptFlowId, entityTypesById, entityTypeIdsByEntityId,
    records, values,

    contactByIdAsObject, businessUnitByIdAsObject, candidateByIdAsObject, vacancyByIdAsObject,

    ...props
}: EntityCreateProps) => {
    const { user, getAccessTokenSilently } = useAuth0();

    const [appMetadata, setAppMetadata] = useState<any>(null);
    useFetch(
        `/api/v2/users/${user?.sub}/app_metadata`,
        {},
        {
            onSuccess: (data) => {
                setAppMetadata(data?.data?.app_metadata);
            }
        },
    );

    const navigate = useNavigate();

    const createRecord = useMemo(() => EntityHelper.create(props, entity.name), [entity.name]);
    const fetchRecord = useMemo(() => EntityHelper.fetchRecordById(props, entity.name), [entity.name]);
    const updateRecord = useMemo(() => EntityHelper.update(props, entity.name), [entity.name]);
    const upsertRecordValues = useMemo(() => EntityHelper.upsertValues(props, entity.name), [entity.name]);
    const { isDeleted, isDeleting, deleteRecordById } = useEntityRecord(entity?.name);

    const initialState = {
        activeStep: STEP.ENTITY_TYPE, // STEP.ENTITY_TYPE
        lastStep: STEP.ENTITY_TYPE,

        record_id: uuidv4(),
        type_id: '',
        showDeleteConfirmation: false,

        isPreparingFlow: true,
        isFetchingConcept: true,
        isCreating: false,
        isConcept: false,
        isSummary: false
    }

    const [state, setState] = useState<any>(initialState);

    interface StepProps {
        id: any,
        state: 'completed' | 'active' | 'inactive' | 'loading',
        title: string,
        description: string,
        isValid: boolean,
        fields?: any
    }

    const [steps, setSteps] = useState<StepProps[]>([]);

    const prepareSteps = (type_id?: string) => {
        let tempSteps: StepProps[] = [
            {
                id: STEP.ENTITY_TYPE,
                state: steps?.find(i => i.id === STEP.ENTITY_TYPE)?.state ?? 'inactive',
                title: `Type`,
                description: `Selecteer het type ${EntityHelper.getSingularDisplayValueByName(entity.name)?.toLowerCase()}`,
                isValid: steps?.find(i => i.id === STEP.ENTITY_TYPE)?.isValid ?? false,
            }
        ];

        if (entity?.relations
            && entity?.relations?.some(i => i?.isActive && i.isEditable)) {
            tempSteps.push({
                id: STEP.ENTITY_RELATION,
                state: steps?.find(i => i.id === STEP.ENTITY_RELATION)?.state ?? 'inactive', // 'loading',
                title: `Gerelateerde entiteiten`,
                description: `Selecteer de gerelateerde entiteiten voor deze ${EntityHelper.getSingularDisplayValueByName(entity.name)}`,
                isValid: steps?.find(i => i.id === STEP.ENTITY_RELATION)?.isValid ?? false
            });
        }

        if (!!entity?.modules?.contact) {
            tempSteps.push({
                id: STEP.MODULE_CONTACT,
                state: steps?.find(i => i.id === STEP.MODULE_CONTACT)?.state ?? 'inactive', // 'loading'
                title: 'Contactgegevens',
                description: 'Bekijk en/of maak nieuwe contactgegevens aan',
                isValid: steps?.find(i => i.id === STEP.MODULE_CONTACT)?.isValid ?? false
            });
        }

        if (entity.name === 'offer') {
            tempSteps.push({
                id: STEP.UPLOAD_FILES,
                state: steps?.find(i => i.id === STEP.UPLOAD_FILES)?.state ?? 'inactive', // 'loading'
                title: 'Bestanden',
                description: 'Upload bestanden',
                isValid: true
            });

            tempSteps.push({
                id: STEP.MAIL,
                state: steps?.find(i => i.id === STEP.MAIL)?.state ?? 'inactive', // 'loading'
                title: 'Mail',
                description: 'Mail',
                isValid: true
            });
        }

        tempSteps.push({
            id: STEP.PRIMARY_FIELDS,
            state: steps?.find(i => i.id === STEP.PRIMARY_FIELDS)?.state ?? 'inactive', // 'loading'
            title: 'Algemeen',
            description: 'Vul de gegeven velden in',
            isValid: steps?.find(i => i.id === STEP.PRIMARY_FIELDS)?.isValid ?? false
        });

        const defaultType = entityTypeIdsByEntityId
            ?.[entity_id]
            ?.map(i => entityTypesById?.[i])
            ?.find(i => !!i.is_default);

        let typeId = defaultType?.id;
        if (type_id) typeId = type_id;

        layouts
            ?.[typeId]
            ?.forEach(i => {
            if (i?.name === entity?.name) return;
            tempSteps.push({
                id: i.id,
                state: steps?.find(j => j.id === i.id)?.state ?? 'inactive', // 'loading'
                title: i.name,
                description: 'Vul de gegeven velden in',
                isValid: steps?.find(j => j.id === i.id)?.isValid ?? false,
                fields: i.fields
            });
        });

        setSteps(tempSteps);
        setState((state) => ({
            ...state,
            isPreparingFlow: false
        }));
    }

    const handleValidation = (id: any) => (isValid: boolean) =>
    {
        const newSteps: StepProps[] = [...(steps ?? [])];
        const newStepsKeys = newSteps.map((i: any) => i.id);

        const idx = newStepsKeys.indexOf(id);
        if (idx === -1) return;

        newSteps[idx].isValid = isValid;

        if (!state.isConcept) {
            newSteps[0].state = 'active';
            setSteps(newSteps);

            return;
        };

        const lastStepIdx = newStepsKeys.indexOf(appMetadata?.concept?.[entity.name]?.lastStep ?? state.activeStep);

        if (id === state.activeStep) {
            newSteps[idx].state = 'active';
        } else if (isValid
            && id !== state.activeStep
            && idx <= lastStepIdx) {
            newSteps[idx].state = 'completed';
        }

        setSteps(newSteps);
    }

    const handleTypeChange = (type_id: string) => {
        if (state.isConcept) {
            const updateRecord = EntityHelper.update(props, entity?.name);
            updateRecord(state.record_id, {
                type_id: type_id
            });

            saveLastStep(steps[0]?.id);
        }

        setState((state) => ({
            ...state,
            type_id: type_id
        }))
    }

    const handleRecordSave = (data: any) => {
        if (!state.isConcept) {
            createRecord({
                id: state.record_id,
                status: STATUS.ACTIVE,
                substatus: SUBSTATUS.ACTIVE.RECORD_CONCEPT,
                type_id: data
            });
            saveLastStep(state.activeStep);
            setState((state) => ({
                ...state,
                isConcept: true
            }));

            return;
        }
    }

    const handleSave = (data: any) => {
        const upsertValueData = Object.keys(data).map((fieldId: any) => ({
            ...data?.[fieldId],
            entity_id: state.record_id,
            field_id: fieldId,
        }));
        upsertRecordValues(state.record_id, upsertValueData);
    }

    const handleCreate = () => {
        resetAppMetadata();

        setState((state) => ({
            ...state,
            isCreating: true
        }));

        updateRecord(state.record_id, {
            status: STATUS.getStatusBySubStatus(SUBSTATUS.getDefaultByEntity(entity?.name)),
            substatus: SUBSTATUS.getDefaultByEntity(entity?.name)
        });
    }

    const resetAppMetadata = async () => {
        props.updateUser(user?.sub, {
            app_metadata: {
                ...appMetadata,
                concept: {
                    ...appMetadata?.concept,
                    [entity.name]: {}
                }
            }
        });
    }

    const saveStep = (stepId: string, isLastStep: boolean = false) => {
        const newAppMetadata = {
            ...appMetadata,
            concept: {
                ...appMetadata?.concept,
                [entity.name]: {
                    ...appMetadata?.concept?.[entity.name],
                    ...(isLastStep ? { lastStep: stepId } : {}),
                    id: state.record_id,
                    activeStep: stepId
                }
            }
        };
        setAppMetadata(newAppMetadata);
        props.updateUser(user?.sub, {
            app_metadata: newAppMetadata
        });
    }

    const saveActiveStep = (stepId: string) => saveStep(stepId);
    const saveLastStep = (stepId: string) => saveStep(stepId, true);

    const handleStepClick = (stepItem: any) => () => {
        if (state.activeStep === stepItem.id) return;

        const currentIdx = steps.findIndex(i => i.id === state.activeStep);
        const newIdx = steps.findIndex(i => i.id === stepItem.id);
        const lastStepIdx = steps.findIndex(i => i.id === appMetadata?.concept?.[entity.name]?.activeStep);
        const lastStateStepIdx = steps.findIndex(i => i.id === state.lastStep);
        const lastStepIndex = lastStateStepIdx >= lastStepIdx ? lastStateStepIdx : lastStepIdx;

        if (newIdx > lastStepIndex) return;

        const newSteps = [...steps];
        newSteps[newIdx].state = 'active';

        newSteps[currentIdx].state = newSteps[currentIdx].isValid ? 'completed' : 'inactive';

        setSteps(newSteps);
        saveActiveStep(stepItem.id);
        setState((state) => ({
            ...state,
            isSummary: false,
            activeStep: stepItem.id
        }));
    }

    const handleNextStep = () => {
        if (state.isSummary) {
            handleCreate();

            return;
        }

        if (state.activeStep === STEP.ENTITY_TYPE) handleRecordSave(state.type_id);

        const idx = steps.findIndex(i => i.id === state.activeStep);
        const newIdx = idx + 1;

        if (newIdx >= steps.length) {
            fetchRecord(state.record_id);

            const newSteps = [...steps];
            newSteps[idx].state = 'completed';
            setSteps(newSteps);
            saveLastStep(steps?.[idx]?.id);
            setState((state) => ({
                ...state,
                isSummary: true
            }));

            return;
        }

        setState((state) => ({
            ...state,
            isSummary: false,
            activeStep: steps?.[newIdx]?.id
        }));

        if (steps[idx].isValid) {
            const newSteps = [...steps];
            newSteps[idx].state = 'completed';
            newSteps[newIdx].state = 'active';
            setSteps(newSteps);

            saveLastStep(steps?.[newIdx]?.id);
        }
    };

    const handlePrevStep = () => {
        if (state.isSummary) {
            setState((state) => ({
                ...state,
                isSummary: false,
                activeStep: steps?.[steps.length-1]?.id
            }));

            return;
        }

        const idx = steps.findIndex(i => i.id === state.activeStep);
        const newIdx = idx - 1;

        if (newIdx < 0) {
            setState((state) => ({
                ...state,
                showDeleteConfirmation: true
            }));

            return;
        }

        setState((state) => ({
            ...state,
            isSummary: false,
            activeStep: steps?.[newIdx]?.id
        }));

        if (steps[idx-1].isValid) {
            const newSteps = [...steps];
            newSteps[idx-1].state = 'active';

            if (newSteps[idx].isValid) {
                newSteps[idx].state = 'completed';
            } else {
                newSteps[idx].state = 'inactive';
            }

            setSteps(newSteps);
            saveActiveStep(steps?.[newIdx]?.id);
        }
    };

    const processDelete = () => {
        if (!state.isConcept) return;
        resetAppMetadata();
        deleteRecordById(state.record_id);
    };

    useEffect(() => {
        if (isDeleted) navigate(EntityHelper.getEntityOverviewEndpointByEntityName(entity.name));
    }, [isDeleted]);

    useEffect(() => {
        if (state.isCreating && records?.[state.record_id]?.status?.substatus !== 'RECORD_CONCEPT') navigate(EntityHelper.getEntityRoute(entity?.name, state.record_id));
    }, [state.isCreating, records?.[state.record_id]?.status]);

    useEffect(() => {
        if (!state.isConcept) return;

        const status = records?.[state?.record_id]?.status?.status;
        const substatus = records?.[state?.record_id]?.status?.substatus;
        if (status === 'ACTIVE' && substatus === 'RECORD_CONCEPT') {
            if (appMetadata) {
                setState((state) => ({
                    ...state,
                    isFetchingConcept: false,
                    activeStep: appMetadata?.concept?.[entity.name]?.activeStep,
                    lastStep: appMetadata?.concept?.[entity.name]?.lastStep,
                    record_id: appMetadata?.concept?.[entity.name]?.id,
                    isConcept: !!appMetadata?.concept?.[entity.name]?.id
                }));
            }
            prepareSteps(records?.[state.record_id]?.type_id);
        } else {
            const id = uuidv4();
            setState((state) => ({
                ...initialState,

                activeStep: STEP.ENTITY_TYPE,
                lastStep: STEP.ENTITY_TYPE,
                record_id: id,
                isConcept: false,
                isFetchingConcept: false
            }));
            prepareSteps();
        }
    }, [records]);

    useEffect(() => {
        if (!appMetadata) return;
        if (state.isConcept) return;

        const conceptId = appMetadata?.concept?.[entity.name]?.id;
        if (conceptId) {
            setState((state) => ({
                ...state,
                isConcept: true,
                record_id: conceptId
            }));
            fetchRecord(conceptId);
            return;
        }

        prepareSteps();

        setState((state) => ({
            ...state,
            isFetchingConcept: false
        }));
    }, [appMetadata]);

    useEffect(() => {
        console.log('appMetadata: ', appMetadata);
        console.log('entity', entity);
        console.log('state', state);
        console.log('records', records);
        console.log('typeId: ', records?.[state.record_id]?.type_id);
        console.log('types: ', entityTypesById);
        console.log('sidebar: ', entityTypesById?.[records?.[state.record_id]?.type_id]?.sidebar_field_ids);
    }, [appMetadata]);

    return <div className={styles['entitycreate']}>
        {
            state.showDeleteConfirmation
            && <DeletionModal
                onClose={() => setState({
                    ...state,
                    showDeleteConfirmation: false
                })}
                onDelete={processDelete}
            >
                Deze { EntityHelper.getSingularDisplayValueByName(entity.name)?.toLowerCase() } zal worden verwijderd, dit is niet te herstellen.
            </DeletionModal>
        }
        <div className={styles['entitycreate__header']}>
            <div className={styles['entitycreate__header-title']}>
                <TitleGroup
                    iconBefore={<EntityIcon
                        entity={entity.name}
                        size='large'
                    />}
                >
                    <Title
                        size='large'
                        weight='bold'
                        transform='uppercase'
                    >
                        { EntityHelper.getSingularDisplayValueByName(entity.name) } toevoegen
                    </Title>
                </TitleGroup>
            </div>
        
            <div className={styles['entitycreate__header-steps']}>
                {
                    steps
                    ?.map(
                        (i: any) =>
                        <EntityCreateStepsDot
                            onClick={handleStepClick(i)}
                            { ...i }
                        />
                    )
                }
            </div>
        </div>
        <div className={styles['entitycreate__content']}>
            {
                state.isFetchingConcept
                ? <Loader description='Laden...' />
                : <Card>
                    {
                        state.isCreating
                        ? <Container padding='vertical'>
                            <Loader description={`${EntityHelper.getSingularDisplayValueByName(entity.name)} toevoegen...`} />
                        </Container>
                        : <>
                            <CardHeader position='sticky'>
                                <TitleGroup gap='small'>
                                    <Title weight='semibold'>{ steps.find(i => i.id === state.activeStep)?.title }</Title>
                                    <Text color='secondary'>{ steps.find(i => i.id === state.activeStep)?.description }</Text>
                                </TitleGroup>
                            </CardHeader>
                            <Container>
                                <div className={styles['entitycreate__content-item']}>
                                    {
                                        !state.isSummary
                                        ? steps?.map((item: any) => {
                                            if (item?.id === STEP.ENTITY_TYPE) {
                                                return <div style={{display: state.activeStep === STEP.ENTITY_TYPE ? 'block' : 'none'}}>
                                                    <EntityTypeStep
                                                        key={item?.name + item?.id}
                                                        entity={entity}
                                                        onSave={handleTypeChange}
                                                        activeType={records?.[state.record_id]?.type_id ?? state.type_id}
                                                        handleValidation={handleValidation(STEP.ENTITY_TYPE)}
                                                        isConcept={state.isConcept}
                                                    />
                                                </div>;
                                            }
        
                                            if (item?.id === STEP.ENTITY_RELATION) {
                                                return <div style={{display: state.activeStep === STEP.ENTITY_RELATION ? 'block' : 'none'}}>
                                                    <EntityRelationStep
                                                        key={item?.name + item?.id}
                                                        entitySetting={entity}
                                                        record_id={state.record_id}
                                                        handleValidation={handleValidation(STEP.ENTITY_RELATION)}
                                                    />
                                                </div>;
                                            }
                                            if (item?.id === STEP.UPLOAD_FILES) {
                                                return <div style={{display: state.activeStep === STEP.UPLOAD_FILES ? 'block' : 'none'}}>
                                                    <Files id={state.record_id} />
                                                </div>;
                                            }

                                            if (item?.id === STEP.MAIL) {
                                                return <div style={{display: state.activeStep === STEP.MAIL ? 'block' : 'none'}}>
                                                    <Mailer id={state.record_id} />
                                                </div>;
                                            }
                                            if (item?.id === STEP.MODULE_CONTACT) {
                                                return <div style={{display: state.activeStep === STEP.MODULE_CONTACT ? 'block' : 'none'}}>
                                                    <EntityCreateContactModule
                                                        key={item?.name + item?.id}
                                                        entity_id={entity.id}
                                                        record_id={state.record_id}
                                                        handleValidation={handleValidation(STEP.MODULE_CONTACT)}
                                                    />
                                                </div>;
                                            }
        
                                            if (item?.id === STEP.PRIMARY_FIELDS) {
                                                return <div style={{display: state.activeStep === STEP.PRIMARY_FIELDS ? 'block' : 'none'}}>
                                                    <CategoryStep
                                                        entity={entity}
                                                        record_id={state.record_id}
                                                        fields={entityTypesById?.[records?.[state.record_id]?.type_id]?.sidebar_field_ids}
                                                        handleValidation={handleValidation(item.id)}
                                                        onSave={handleSave}
                                                        values={values?.[state.record_id]}
                                                    />
                                                </div>;
                                            }
        
                                            return <div style={{display: state.activeStep === item.id ? 'block' : 'none'}}>
                                                <CategoryStep
                                                    entity={entity}
                                                    record_id={state.record_id}
                                                    fields={item.fields}
                                                    handleValidation={handleValidation(item.id)}
                                                    onSave={handleSave}
                                                    values={values?.[state.record_id]}
                                                />
                                            </div>;
                                        })
                                        : steps.map((item: any) =>
                                            <FormSection title={item.description} >
                                                {
                                                    item.id === STEP.ENTITY_TYPE &&
                                                    entityTypesById?.[records?.[state.record_id]?.type_id]?.name
                                                }
                                                {
                                                    item.id === STEP.DISPLAY_NAME &&
                                                    <FormGroup title='Weergave naam'>
                                                        <DisplayName
                                                            entity={entity}
                                                            entity_id={state.record_id}
                                                        />
                                                    </FormGroup>
                                                }
                                                {
                                                    item.id === STEP.UPLOAD_FILES &&
                                                    <Files id={state.record_id} disableAdd />
                                                }
                                                {
                                                    item.id === STEP.ENTITY_RELATION &&
                                                    <EntityRelationOverview
                                                        entitySetting={entity}
                                                        record_id={state.record_id}
                                                    />
                                                }
                                                {
                                                    item.id === STEP.MODULE_CONTACT
                                                    && <Contact
                                                        id={state.record_id}
                                                        entity_id={entity.id}
                                                        isEdit={false}
                                                    />
                                                }
                                                {
                                                    item.id === STEP.PRIMARY_FIELDS
                                                    && !!entityTypesById?.[records?.[state.record_id]?.type_id]?.sidebar_field_ids
                                                    && <CategoryOverview
                                                        fields={entityTypesById?.[records?.[state.record_id]?.type_id]?.sidebar_field_ids}
                                                        values={values?.[state.record_id]}
                                                    />
                                                }
                                                { (item.id !== STEP.ENTITY_TYPE
                                                && item.id !== STEP.DISPLAY_NAME
                                                && item.id !== STEP.ENTITY_RELATION)
                                                && <CategoryOverview
                                                    fields={item.fields}
                                                    values={values?.[state.record_id]}
                                                /> }
                                            </FormSection>
                                        )
                                    }
                                </div>
                            </Container>
                            <CardFooter position='sticky'>
                                <ButtonGroup>
                                    {
                                        state.isConcept &&
                                        <Button
                                            size='large'
                                            onClick={handlePrevStep}
                                            isLoading={isDeleting}
                                        >
                                            {
                                                state.activeStep !== STEP.ENTITY_TYPE
                                                ? 'Vorige stap'
                                                : 'Concept verwijderen'
                                            }
                                        </Button>
                                    }
                                    <Button
                                        onClick={handleNextStep}
                                        color='primary'
                                        variant='solid'
                                        size='large'
        
                                        isDisabled={!steps.find(i => i.id === state.activeStep)?.isValid || isDeleting}
                                    >
                                        {
                                            state.isSummary
                                            ? 'Toevoegen'
                                            : state.activeStep === steps[steps.length-1].id
                                            ? 'Samenvatting'
                                            : 'Volgende stap'
                                        }
                                    </Button>
                                </ButtonGroup>
                            </CardFooter>
                        </>
                    }
                </Card>
            }
        </div>
    </div>;
}

interface Props {
    children?: any
    entity_id: string
    record_id?: string
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),

    entitiesById: getEntitiesById(state),
    entityTypesById: getEntityTypesByIdAsObject(state),
    entityTypeIdsByEntityId: getEntityTypesByEntityIdAsObject(state),
    fieldsById: getFieldsById(state),

    layouts: getLayoutsByEntityTypeIdAsObject(state),

    isEntityFetching: isEntityFetchingByEntityName(state, getEntityById(state, props.entity_id)?.name),
    conceptFlowId: selectConceptRecordIdByEntityName(state, getEntityById(state, props.entity_id)?.name),
    values: selectValuesByIdsByEntityName(state, getEntityById(state, props.entity_id)?.name),

    contactByIdAsObject: getContactByIdAsObject(state),
    businessUnitByIdAsObject: getBusinessUnitByIdAsObject(state),
    candidateByIdAsObject: getCandidateByIdAsObject(state),
    vacancyByIdAsObject: getVacancyByIdAsObject(state),
    offerByIdAsObject: getOfferByIdAsObject(state),

    records: selectRecordsByIdsByEntityName(state, getEntityById(state, props.entity_id)?.name)
});

const mapDispatch = {
    updateUser,

    createContact,
    createBusinessUnit,
    createCandidate,
    createVacancy,
    createOffer,

    fetchContactConcept,
    fetchBusinessUnitConcept,
    fetchCandidateConcept,
    fetchVacancyConcept,
    fetchOfferConcept,

    fetchContactV2,
    fetchBusinessUnitV2,
    fetchCandidateV2,
    fetchVacancyV2,
    fetchOffer,

    fetchContactValues,
    fetchBusinessUnitValues,
    fetchCandidateValues,
    fetchVacancyValues,
    fetchOfferValues,

    updateContact,
    updateBusinessUnit,
    updateCandidate,
    updateVacancy,
    updateOffer,

    upsertContactValues,
    upsertBusinessUnitValues,
    upsertCandidateValues,
    upsertVacancyValues,
    upsertOfferValues,

    deleteContact,
    deleteBusinessUnit,
    deleteCandidate,
    deleteVacancy,
    deleteOffer
};

type EntityCreateProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityCreate);